import { useLogout, useGetIdentity, useMenu, useGo, useCustom, useApiUrl } from '@refinedev/core';
import { Link, useLocation } from 'react-router-dom';
import { useMemo, useState, useCallback } from 'react';
import { useMenuPermissions } from '@/hooks/usePermission';
import { useSidebarCounts, type SidebarCounts } from '@/hooks/use-sidebar-counts';

import {
  LayoutDashboard,
  Users,
  ShieldCheck,
  CreditCard,
  FileText,
  Megaphone,
  HelpCircle,
  Settings,
  BarChart3,
  LogOut,
  UserCircle,
  UserCog,
  Receipt,
  GraduationCap,
  Image,
  Crown,
  Globe,
  Languages,
  Zap,
  Send,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  MoreVertical,
  Key,
  Bot,
  Moon,
  Sun,
  Ticket,
  MessageSquare,
  Shield,
  Flag,
  Coins,
  UserPlus,
  AlertTriangle,
  MailCheck,
  BrainCircuit,
  Mic,
  ClipboardList,
  Scale,
  Radio,
  Tag,
  Link2,
  Workflow,
  TicketPercent,
  Database,
  BotMessageSquare,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from 'next-themes';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TelegramSessionsMenu } from './telegram-sessions-menu';
import { useTenant } from '@/providers/tenant-context';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

interface LeadFunnelMenuItem {
  id: number;
  name: string;
  position: number;
  isActive: boolean;
}

interface UserIdentity {
  id: number;
  name: string;
  email: string;
  role: string;
}

const iconMap: Record<string, React.ReactNode> = {
  // Main
  dashboard: <LayoutDashboard className="h-4 w-4" />,

  // Users
  'admin-users': <UserCog className="h-4 w-4" />,
  users: <Users className="h-4 w-4" />,
  roles: <ShieldCheck className="h-4 w-4" />,
  'user-invitations': <UserPlus className="h-4 w-4" />,

  // Commerce
  'user-entitlements': <ShieldCheck className="h-4 w-4" />,
  subscriptions: <CreditCard className="h-4 w-4" />,
  'subscription-plans': <Ticket className="h-4 w-4" />,
  'channel-passes': <Ticket className="h-4 w-4" />,
  payments: <CreditCard className="h-4 w-4" />,
  orders: <Receipt className="h-4 w-4" />,
  'admin/currencies': <Coins className="h-4 w-4" />,

  // Content
  courses: <GraduationCap className="h-4 w-4" />,
  books: <BookOpen className="h-4 w-4" />,
  banners: <Image className="h-4 w-4" />,
  announcements: <Megaphone className="h-4 w-4" />,
  'mini-banners': <Image className="h-4 w-4" />,
  teachers: <Crown className="h-4 w-4" />,
  'club-settings': <Settings className="h-4 w-4" />,

  'legal-pages': <Scale className="h-4 w-4" />,

  // Social
  posts: <FileText className="h-4 w-4" />,
  moderation: <Shield className="h-4 w-4" />,
  reports: <Flag className="h-4 w-4" />,
  violations: <AlertTriangle className="h-4 w-4" />,

  // Communications
  broadcasts: <Megaphone className="h-4 w-4" />,
  triggers: <Zap className="h-4 w-4" />,
  'message-templates': <MailCheck className="h-4 w-4" />,
  livestreams: <Radio className="h-4 w-4" />,

  // Telegram
  'bot-tokens': <Bot className="h-4 w-4" />,
  'translation-keys/bot': <Bot className="h-4 w-4" />,
  'telegram-channels': <Send className="h-4 w-4" />,
  'telegram-bots': <Bot className="h-4 w-4" />,
  'channel-memberships': <Users className="h-4 w-4" />,
  'telegram-client': <MessageSquare className="h-4 w-4" />,

  // AI & Analytics
  ai: <BrainCircuit className="h-4 w-4" />,
  'knowledge-documents': <BookOpen className="h-4 w-4" />,
  'voice-profiles': <Mic className="h-4 w-4" />,
  // Admin
  'audit-logs': <ClipboardList className="h-4 w-4" />,
  languages: <Languages className="h-4 w-4" />,
  'translation-keys/ui': <Globe className="h-4 w-4" />,
  integrations: <Key className="h-4 w-4" />,
  mcp: <BotMessageSquare className="h-4 w-4" />,
  'support-tickets': <HelpCircle className="h-4 w-4" />,
  messaging: <MessageSquare className="h-4 w-4" />,

  // Promo Codes
  'promo-codes': <TicketPercent className="h-4 w-4" />,

  // Tags & UTM
  tags: <Tag className="h-4 w-4" />,
  'utm-sources': <Link2 className="h-4 w-4" />,
  'admin/countries': <Globe className="h-4 w-4" />,
  databases: <Database className="h-4 w-4" />,

  // CRM
  leads: <UserPlus className="h-4 w-4" />,
  'lead-webhooks': <Link2 className="h-4 w-4" />,
  'lead-funnels': <Workflow className="h-4 w-4" />,
};

// Группы с иконками для заголовков
const menuGroups = {
  main: ['dashboard'],

  content: ['posts', 'teachers', 'banners', 'announcements', 'mini-banners', 'livestreams', 'club-settings', 'legal-pages'],

  commerce: ['user-entitlements', 'courses', 'books', 'subscription-plans', 'channel-passes', 'orders', 'payments', 'admin/currencies', 'promo-codes'],

  users: ['users', 'user-invitations', 'tags', 'messaging', 'support-tickets', 'telegram-client'],

  crm: ['lead-funnels', 'lead-webhooks'],

  social: ['moderation', 'reports', 'violations'],

  communications: ['broadcasts', 'triggers', 'message-templates'],

  telegram: ['bot-tokens', 'translation-keys/bot', 'telegram-channels', 'telegram-bots'],

  analytics: ['ai', 'knowledge-documents', 'voice-profiles'],

  admin: ['admin-users', 'roles', 'languages', 'translation-keys/ui', 'integrations', 'mcp', 'audit-logs', 'utm-sources', 'admin/countries', 'databases'],
};

const groupLabels: Record<string, string> = {
  main: 'Главная',
  users: 'Пользователи',
  crm: 'CRM',
  content: 'Контент',
  commerce: 'Коммерция',
  social: 'Соцсеть',
  communications: 'Рассылки',
  telegram: 'Telegram',
  analytics: 'AI',
  admin: 'Администрирование',
};

const groupIcons: Record<string, React.ReactNode> = {
  main: <LayoutDashboard className="h-4 w-4" />,
  users: <Users className="h-4 w-4" />,
  crm: <Workflow className="h-4 w-4" />,
  content: <GraduationCap className="h-4 w-4" />,
  commerce: <CreditCard className="h-4 w-4" />,
  social: <Shield className="h-4 w-4" />,
  communications: <Megaphone className="h-4 w-4" />,
  telegram: <Send className="h-4 w-4" />,
  analytics: <BarChart3 className="h-4 w-4" />,
  admin: <Settings className="h-4 w-4" />,
};

// Кастомные лейблы для ресурсов
const customLabels: Record<string, string> = {
  'translation-keys/bot': 'Сообщения бота',
  'translation-keys/ui': 'Локализация UI',
  'admin/currencies': 'Валюты',
  'utm-sources': 'UTM источники',
  'admin/countries': 'Страны',
};

const STORAGE_KEY = 'sidebar-collapsed-groups';

function getInitialCollapsedGroups(): Set<string> {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return new Set(JSON.parse(saved));
    }
  } catch {
    // ignore
  }
  return new Set<string>();
}

function saveCollapsedGroups(groups: Set<string>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...groups]));
  } catch {
    // ignore
  }
}

interface SidebarProps {
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  className?: string;
  isMobile?: boolean;
}

export function Sidebar({
  isCollapsed = false,
  onToggleCollapse,
  className,
  isMobile = false,
}: SidebarProps) {
  const { resolvedTheme, setTheme } = useTheme();
  const { activeTenant } = useTenant();
  const { menuItems } = useMenu();
  const go = useGo();
  const apiUrl = useApiUrl();
  const location = useLocation();
  const { mutate: logout } = useLogout();
  const { data: identity } = useGetIdentity<UserIdentity>();
  const leadFunnelsQuery = useCustom<LeadFunnelMenuItem[]>({
    url: `${apiUrl}/leads/funnels`,
    method: 'get',
  });
  const leadFunnels = useMemo(() => {
    return [...(leadFunnelsQuery.query?.data?.data ?? [])]
      .filter((funnel) => funnel.isActive)
      .sort((a, b) => a.position - b.position || a.id - b.id);
  }, [leadFunnelsQuery.query?.data?.data]);

  const { data: sidebarCounts } = useSidebarCounts();

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(getInitialCollapsedGroups);

  const getItemCount = useCallback((resourceName: string): number => {
    if (!sidebarCounts) { return 0; }
    return sidebarCounts[resourceName as keyof SidebarCounts] ?? 0;
  }, [sidebarCounts]);

  const getGroupCount = useCallback((group: string): number => {
    if (!sidebarCounts) { return 0; }
    const groupNames = menuGroups[group as keyof typeof menuGroups] ?? [];
    return groupNames.reduce((sum, name) => sum + (sidebarCounts[name as keyof SidebarCounts] ?? 0), 0);
  }, [sidebarCounts]);

  const toggleGroup = useCallback((group: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      saveCollapsedGroups(next);
      return next;
    });
  }, []);

  const allResourceNames = useMemo(() => {
    return menuItems.map((item) => item.name ?? '').filter(Boolean);
  }, [menuItems]);

  const { permissionMap, isLoading: permissionsLoading } =
    useMenuPermissions(allResourceNames);

  const getMenuItemsByGroup = (group: string) => {
    const groupNames = menuGroups[group as keyof typeof menuGroups] ?? [];
    return menuItems.filter((item) => groupNames.includes(item.name ?? ''));
  };

  const isActive = (route: string) => {
    if (route === '/' && location.pathname === '/') { return true; }
    if (route !== '/' && location.pathname.startsWith(route)) { return true; }
    return false;
  };

  const hasActiveChild = (group: string) => {
    const items = getMenuItemsByGroup(group);
    return items.some((item) => isActive(item.route ?? `/${item.name}`));
  };

  const handleNavigation = (route: string) => {
    go({ to: route });
  };

  const getLabel = (item: { name?: string; label?: string }) => {
    if (item.name && customLabels[item.name]) {
      return customLabels[item.name];
    }
    return item.label;
  };

  return (
    <div
      className={cn(
        'relative flex h-full flex-col overflow-y-auto bg-card transition-all duration-300 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]',
        !isMobile && 'border-r',
        isCollapsed && !isMobile ? 'w-[70px]' : (isMobile ? 'w-full' : 'w-64'),
        className
      )}
    >
      {/* Header / Toggle */}
      <div className="sticky top-0 z-10 flex h-16 shrink-0 items-center justify-between border-b border-border bg-card px-4">
        <Link
          to="/"
          className={cn(
            'flex items-center gap-2 overflow-hidden transition-all',
            isCollapsed && !isMobile ? 'w-0 opacity-0' : 'w-auto opacity-100'
          )}
        >
          <img
            src={
              activeTenant?.logoUrl
                ? activeTenant.logoUrl
                : resolvedTheme === 'dark'
                  ? '/logo-white.svg'
                  : '/logo-black.svg'
            }
            alt={activeTenant?.name ?? 'Masters Club'}
            className="h-8 w-auto"
          />
        </Link>

        {!isMobile && (
          <Button
            variant="ghost"
            size="icon"
            className={cn('hidden lg:flex', isCollapsed && 'mx-auto')}
            onClick={onToggleCollapse}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-2">
        <TooltipProvider delayDuration={0}>
          <nav className="flex flex-col gap-1 px-2">
            {Object.entries(menuGroups).map(([group]) => {
              const items = getMenuItemsByGroup(group);
              if (items.length === 0) { return null; }

              const isMainGroup = group === 'main';
              const isGroupOpen = !collapsedGroups.has(group);
              const active = hasActiveChild(group);

              // Dashboard — без группировки
              if (isMainGroup) {
                const item = items[0];
                if (!item) { return null; }
                const route = item.route ?? `/${item.name}`;
                const itemActive = isActive(route);
                const icon = iconMap[item.name ?? ''] ?? <FileText className="h-4 w-4" />;
                const hasPermission = permissionsLoading || permissionMap.get(item.name ?? '') !== false;

                if (isCollapsed && !isMobile) {
                  return (
                    <Tooltip key="dashboard">
                      <TooltipTrigger asChild>
                        <Button
                          variant={itemActive ? 'secondary' : 'ghost'}
                          size="icon"
                          className={cn(
                            'w-full',
                            itemActive && 'bg-secondary',
                            !hasPermission && 'opacity-40 cursor-not-allowed'
                          )}
                          onClick={() => hasPermission && handleNavigation(route)}
                          disabled={!hasPermission}
                        >
                          {icon}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        {hasPermission ? getLabel(item) : `${getLabel(item)} (нет доступа)`}
                      </TooltipContent>
                    </Tooltip>
                  );
                }

                return (
                  <Button
                    key="dashboard"
                    variant={itemActive ? 'secondary' : 'ghost'}
                    className={cn(
                      'w-full justify-start gap-2 mb-1',
                      itemActive && 'bg-secondary',
                      !hasPermission && 'opacity-40 cursor-not-allowed'
                    )}
                    onClick={() => hasPermission && handleNavigation(route)}
                    disabled={!hasPermission}
                  >
                    {icon}
                    <span className="truncate">{getLabel(item)}</span>
                  </Button>
                );
              }

              // Collapsed sidebar — показываем только иконку группы
              if (isCollapsed && !isMobile) {
                const groupBadgeCount = getGroupCount(group);
                return (
                  <Tooltip key={group}>
                    <TooltipTrigger asChild>
                      <Button
                        variant={active ? 'secondary' : 'ghost'}
                        size="icon"
                        className={cn('w-full relative', active && 'bg-secondary')}
                        onClick={() => {
                          // При клике на свёрнутую группу — переходим к первому доступному пункту
                          const firstAccessible = items.find((item) =>
                            permissionsLoading || permissionMap.get(item.name ?? '') !== false
                          );
                          if (firstAccessible) {
                            handleNavigation(firstAccessible.route ?? `/${firstAccessible.name}`);
                          }
                        }}
                      >
                        {groupIcons[group] ?? <FileText className="h-4 w-4" />}
                        {groupBadgeCount > 0 && (
                          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-muted-foreground px-1 text-[9px] font-medium text-white">
                            {groupBadgeCount > 99 ? '99+' : groupBadgeCount}
                          </span>
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      {groupLabels[group]}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              // Раскрывающаяся группа
              return (
                <Collapsible
                  key={group}
                  open={isGroupOpen}
                  onOpenChange={() => toggleGroup(group)}
                >
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
                        active && !isGroupOpen && 'text-foreground bg-accent/50'
                      )}
                    >
                      {groupIcons[group] ?? <FileText className="h-4 w-4" />}
                      <span className="flex-1 text-left truncate">{groupLabels[group]}</span>
                      {!isGroupOpen && getGroupCount(group) > 0 && (
                        <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-muted-foreground px-1.5 text-[10px] font-medium text-white">
                          {getGroupCount(group) > 99 ? '99+' : getGroupCount(group)}
                        </span>
                      )}
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 shrink-0 transition-transform duration-200',
                          !isGroupOpen && '-rotate-90'
                        )}
                      />
                    </button>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="ml-3 mt-1 space-y-0.5 border-l border-border pl-3">
                      {items.map((item) => {
                        const route = item.route ?? `/${item.name}`;
                        const itemActive = isActive(route);
                        const icon = iconMap[item.name ?? ''] ?? <FileText className="h-4 w-4" />;
                        const label = getLabel(item);
                        const hasPermission =
                          permissionsLoading || permissionMap.get(item.name ?? '') !== false;

                        const badgeCount = getItemCount(item.name ?? '');

                        return (
                          <div key={item.key}>
                            <Button
                              variant={itemActive ? 'secondary' : 'ghost'}
                              size="sm"
                              className={cn(
                                'w-full justify-start gap-2 h-8',
                                itemActive && 'bg-secondary',
                                !hasPermission && 'opacity-40 cursor-not-allowed'
                              )}
                              onClick={() => hasPermission && handleNavigation(route)}
                              disabled={!hasPermission}
                            >
                              {icon}
                              <span className="truncate text-xs flex-1 text-left">{label}</span>
                              {badgeCount > 0 && (
                                <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-muted-foreground px-1.5 text-[10px] font-medium text-white">
                                  {badgeCount > 99 ? '99+' : badgeCount}
                                </span>
                              )}
                            </Button>
                          {item.name === 'lead-funnels' && leadFunnels.length > 0 && (
                            <div className="ml-6 mt-0.5 space-y-0.5">
                              {leadFunnels.map((funnel) => {
                                const funnelRoute = `/leads/funnels/${funnel.id}`;
                                const funnelActive = isActive(funnelRoute);
                                return (
                                  <Button
                                    key={funnel.id}
                                    variant={funnelActive ? 'secondary' : 'ghost'}
                                    size="sm"
                                    className={cn(
                                      'h-7 w-full justify-start px-2 text-xs',
                                      funnelActive && 'bg-secondary',
                                    )}
                                    onClick={() => handleNavigation(funnelRoute)}
                                  >
                                    <span className="truncate text-left">{funnel.name}</span>
                                  </Button>
                                );
                              })}
                            </div>
                          )}
                          </div>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </nav>
        </TooltipProvider>
      </ScrollArea>

      {/* User section */}
      <div className="sticky bottom-0 z-10 border-t bg-card p-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className={cn(
                'w-full',
                isCollapsed && !isMobile
                  ? 'justify-center px-0'
                  : 'justify-start px-2'
              )}
            >
              <div className="flex items-center gap-2 w-full">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary/10 text-primary">
                    {identity?.email?.substring(0, 2).toUpperCase() || 'AD'}
                  </AvatarFallback>
                </Avatar>

                {(!isCollapsed || isMobile) && (
                  <div className="flex flex-col items-start min-w-0 flex-1 text-left">
                    <span className="text-sm font-medium truncate w-full">
                      {identity?.email}
                    </span>
                    <span className="text-xs text-muted-foreground truncate w-full capitalize">
                      {identity?.role}
                    </span>
                  </div>
                )}

                {(!isCollapsed || isMobile) && (
                  <MoreVertical className="h-4 w-4 text-muted-foreground ml-auto" />
                )}
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-56"
            side={isCollapsed && !isMobile ? 'right' : 'top'}
          >
            <DropdownMenuLabel>Мой аккаунт</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled>
              <UserCircle className="mr-2 h-4 w-4" />
              <span>Профиль</span>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleNavigation('/integrations')}>
              <Settings className="mr-2 h-4 w-4" />
              <span>Интеграции</span>
            </DropdownMenuItem>
            <TelegramSessionsMenu />
            <DropdownMenuItem
              onClick={(e) => {
                e.preventDefault();
                setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
              }}
            >
              {resolvedTheme === 'dark' ? (
                <Sun className="mr-2 h-4 w-4" />
              ) : (
                <Moon className="mr-2 h-4 w-4" />
              )}
              <span>{resolvedTheme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => logout()}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span>Выйти</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
